import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getInstallation, getInstallationLogbook } from "../../services/fireSafetyLogbookApi";
import { useUser } from "../../context/UserContext";
import {
  getCustomerPortalInstallation,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

export default function PortalInstallationDetailPage() {
  const { user } = useUser();
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";

  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [installation, setInstallation] = useState(null);
  const [entries, setEntries] = useState([]);
  const [workorders, setWorkorders] = useState([]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (isCustomerPortalUser) {
          const detail = await getCustomerPortalInstallation(id);
          const portalItem = detail?.item || null;
          if (!active) return;
          setInstallation(portalItem);
          setEntries(Array.isArray(portalItem?.logbookEntries) ? portalItem.logbookEntries : []);
          setWorkorders(Array.isArray(portalItem?.workorders) ? portalItem.workorders : []);
          return;
        }

        const detail = await getInstallation(id);
        const installationData = detail?.installation || null;
        let logbookEntries = Array.isArray(detail?.logbookEntries) ? detail.logbookEntries : [];

        if (!logbookEntries.length && installationData?._id) {
          logbookEntries = await getInstallationLogbook(installationData._id);
        }

        if (!active) return;
        setInstallation(installationData);
        setEntries(Array.isArray(logbookEntries) ? logbookEntries : []);
        setWorkorders(Array.isArray(detail?.workorders) ? detail.workorders : []);
      } catch (err) {
        if (!active) return;
        setError(
          isCustomerPortalUser
            ? toPortalErrorMessage(err, "Installatie detail laden mislukt")
            : err?.response?.data?.message || "Installatie detail laden mislukt"
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    if (id) {
      load();
    } else {
      setLoading(false);
      setError("Ongeldige installatie");
    }

    return () => {
      active = false;
    };
  }, [id, isCustomerPortalUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
          Laden...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <Link to="/portaal/installaties" className="hover:underline">Installaties</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">{installation?.name || installation?.naam || "Detail"}</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">{installation?.name || installation?.naam || "Installatie"}</h1>
          <p className="text-sm text-slate-600">{formatLocation(installation)}</p>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        {installation && (
          <section className="grid gap-4 lg:grid-cols-2">
            <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">Overzicht</h2>
              <div className="mt-3 grid gap-2 text-sm text-slate-700">
                <p><span className="text-slate-500">Type:</span> {formatType(installation?.type)}</p>
                <p><span className="text-slate-500">Locatie:</span> {formatLocation(installation)}</p>
                <p><span className="text-slate-500">Service-ID:</span> {installation?.serviceId || "-"}</p>
                <p><span className="text-slate-500">Logboek Access:</span> {installation?.qrEnabled ? "Actief" : "Nog niet actief"}</p>
                {!isCustomerPortalUser ? (
                  <p><span className="text-slate-500">Logboek Access QR-token:</span> {installation?.qrToken || "-"}</p>
                ) : null}
                <p><span className="text-slate-500">Aangemaakt:</span> {formatDateTime(installation?.createdAt)}</p>
                <p><span className="text-slate-500">Laatst bijgewerkt:</span> {formatDateTime(installation?.updatedAt)}</p>
              </div>
            </article>

            <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">BAG gegevens</h2>
              <div className="mt-3 grid gap-2 text-sm text-slate-700">
                <p>
                  <span className="text-slate-500">Nummeraanduiding:</span>{" "}
                  {installation?.bagAddress?.nummeraanduidingIdentificatie || "-"}
                </p>
                <p>
                  <span className="text-slate-500">Adresseerbaar object:</span>{" "}
                  {installation?.bagAddress?.adresseerbaarObjectIdentificatie || "-"}
                </p>
                <p><span className="text-slate-500">Woonplaats:</span> {installation?.bagAddress?.woonplaatsNaam || "-"}</p>
                <p><span className="text-slate-500">Panden:</span> {installation?.bagAddress?.pandIdentificaties?.length || 0}</p>
              </div>
            </article>
          </section>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">Gekoppelde werkbonnen</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{workorders.length}</span>
          </div>

          {workorders.length === 0 ? (
            <p className="text-sm text-slate-500">Nog geen gekoppelde werkbonnen.</p>
          ) : (
            <div className="space-y-2">
              {workorders.map((item) => (
                <Link
                  key={String(item?.id || item?._id || "")}
                  to={
                    isCustomerPortalUser
                      ? `/portaal/werkbonnen/${item?.id || item?._id}`
                      : `/werkbon/${item?._id || item?.id}`
                  }
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 hover:border-blue-300 hover:bg-blue-50"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item?.workorderNumber || item?.werkbonNummer || item?.nummer || "-"}</p>
                    <p className="text-xs text-slate-600">{item?.title || item?.omschrijving || "Geen omschrijving"}</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    {item?.statusLabel || formatStatus(item?.status)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">Logboek</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{entries.length}</span>
          </div>

          {entries.length === 0 ? (
            <p className="text-sm text-slate-500">Nog geen logboekregels.</p>
          ) : (
            <div className="space-y-2">
              {entries.slice(0, 20).map((entry) => (
                <article
                  key={String(entry?._id || `${entry?.performedAt || ""}-${entry?.entryType || ""}`)}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                >
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{formatEntryType(entry?.entryType)}</p>
                    <p className="text-xs text-slate-500">{formatDateTime(entry?.performedAt || entry?.createdAt)}</p>
                  </div>
                  <p className="text-sm text-slate-700">{entry?.description || "-"}</p>
                  {Array.isArray(entry?.attachments) && entry.attachments.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {entry.attachments.map((attachment) => (
                        <a
                          key={String(attachment?.id || attachment?._id || "")}
                          href={attachment?.url || "#"}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                        >
                          {attachment?.name || "Bijlage"}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function formatType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (!type) return "-";
  if (type === "bmi") return "BMI";
  if (type === "oai") return "OAI";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatLocation(installation) {
  return (
    installation?.locationLabel ||
    installation?.locatie ||
    installation?.bagAddress?.formattedAddress ||
    "Locatie onbekend"
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "-";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatEntryType(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "Logboekregel";
  const labels = {
    onderhoud: "Onderhoud",
    storing: "Storing",
    inspectie: "Inspectie",
    test: "Test",
    oplevering: "Oplevering",
    overig: "Overig",
    correctie: "Correctie",
  };
  return labels[key] || formatStatus(key);
}
